import React, { useCallback, useEffect, useRef, useState } from 'react';
import './mapView.css';
import type { ReportMap, ReportMapStop } from '../services/api';

interface MapViewProps {
  map: ReportMap;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const normalizeTitle = (value: string): string =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const DEPTH_LABELS: Record<string, string> = {
  quick: 'Quick read',
  standard: 'Standard read',
  deep: 'Deep dive',
};

const PULL_THRESHOLD = 110;
const CHIP_CLASS = 'rmap-origin-chip';
const FLASH_CLASS = 'rmap-target-flash';

/**
 * Interpretive map rendered as a front layer over the traditional report.
 * Pull down (or use a stop) to descend into the report; a floating pill
 * brings the reader back to the map.
 */
const MapView: React.FC<MapViewProps> = ({ map, scrollContainerRef }) => {
  const [descended, setDescended] = useState(false);
  const [exploredStopIds, setExploredStopIds] = useState<Set<string>>(new Set());
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartYRef = useRef<number | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  const stops = map.stops || [];
  const threads = map.threads || [];

  const removeInjectedChip = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.querySelectorAll(`.${CHIP_CLASS}`).forEach((el) => el.remove());
    container.querySelectorAll(`.${FLASH_CLASS}`).forEach((el) => el.classList.remove(FLASH_CLASS));
  }, [scrollContainerRef]);

  useEffect(() => removeInjectedChip, [removeInjectedChip]);

  const findSectionElement = useCallback(
    (sectionTitle: string): HTMLElement | null => {
      const container = scrollContainerRef.current;
      if (!container) return null;
      const target = normalizeTitle(sectionTitle);
      if (!target) return null;
      const headings = Array.from(container.querySelectorAll<HTMLElement>('.report-section .section-heading'));
      let match = headings.find((h) => normalizeTitle(h.textContent || '') === target);
      if (!match) {
        match = headings.find((h) => {
          const heading = normalizeTitle(h.textContent || '');
          return heading.includes(target) || target.includes(heading);
        });
      }
      return match ? (match.closest('.report-section') as HTMLElement | null) : null;
    },
    [scrollContainerRef]
  );

  const resurface = useCallback(() => {
    removeInjectedChip();
    setDescended(false);
    setDragOffset(0);
  }, [removeInjectedChip]);

  const descend = useCallback(
    (sectionTitle: string | null, stop?: ReportMapStop) => {
      if (stop) {
        setExploredStopIds((prev) => {
          if (prev.has(stop.id)) return prev;
          const next = new Set(prev);
          next.add(stop.id);
          return next;
        });
      }
      setDescended(true);
      setDragOffset(0);

      // Wait a frame so the report behind is interactable before scrolling.
      window.setTimeout(() => {
        removeInjectedChip();
        const container = scrollContainerRef.current;
        if (!container) return;
        if (!sectionTitle) {
          container.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const section = findSectionElement(sectionTitle);
        if (!section) {
          container.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = CHIP_CLASS;
        const originLabel = stop ? stop.title : 'the Brief';
        chip.innerHTML = `<span class="rmap-origin-chip-arrow">&#8963;</span> From the Brief &mdash; &ldquo;${originLabel.replace(/</g, '&lt;')}&rdquo; &middot; back to the Brief`;
        chip.addEventListener('click', () => resurface());
        section.insertBefore(chip, section.firstChild);
        section.classList.add(FLASH_CLASS);
        window.setTimeout(() => section.classList.remove(FLASH_CLASS), 2200);

        const containerRect = container.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const top = container.scrollTop + (sectionRect.top - containerRect.top) - 18;
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }, 60);
    },
    [findSectionElement, removeInjectedChip, resurface, scrollContainerRef]
  );

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    dragStartYRef.current = event.clientY;
    setIsDragging(true);
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }, []);

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    if (dragStartYRef.current === null) return;
    const dy = Math.max(0, event.clientY - dragStartYRef.current);
    setDragOffset(dy);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (dragStartYRef.current === null) return;
    const shouldDescend = dragOffset > PULL_THRESHOLD;
    dragStartYRef.current = null;
    setIsDragging(false);
    if (shouldDescend) {
      descend(null);
    } else {
      setDragOffset(0);
    }
  }, [descend, dragOffset]);

  const exploredCount = stops.filter((s) => exploredStopIds.has(s.id)).length;

  const layerStyle: React.CSSProperties = descended
    ? { transform: 'translateY(calc(100% + 40px))' }
    : dragOffset > 0
      ? { transform: `translateY(${dragOffset}px)`, transition: isDragging ? 'none' : undefined }
      : {};

  return (
    <>
      <div
        ref={layerRef}
        className={`rmap-layer ${descended ? 'rmap-layer-descended' : ''}`}
        style={layerStyle}
        aria-hidden={descended}
      >
        <div className="rmap-scroll">
          <article className="rmap-page">
            <div
              className="rmap-pull-handle"
              role="button"
              tabIndex={0}
              title="Pull down to read the full analysis"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onClick={() => {
                if (dragOffset === 0) descend(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  descend(null);
                }
              }}
            >
              <span className="rmap-pull-grip" />
              <span className="rmap-pull-label">Pull down for the full analysis</span>
            </div>

            <header className="rmap-header">
              <span className="rmap-eyebrow">Analysis Brief</span>
              <h1 className="rmap-title">{map.map_title || 'What this analysis found'}</h1>
              <div className="rmap-meta">
                <span>{stops.length} key findings</span>
                {typeof map.reading_minutes === 'number' && map.reading_minutes > 0 && (
                  <span>&middot; full analysis ~{map.reading_minutes} min</span>
                )}
              </div>
            </header>

            {map.orientation && (
              <div className="rmap-orientation">
                <span className="rmap-orientation-label">Bottom line</span>
                <p>{map.orientation}</p>
              </div>
            )}

            <div className="rmap-stops">
              {stops.map((stop, idx) => {
                const explored = exploredStopIds.has(stop.id);
                const depthLabel = stop.depth ? DEPTH_LABELS[stop.depth] || stop.depth : null;
                const timeParts: string[] = [];
                if (typeof stop.minutes === 'number' && stop.minutes > 0) {
                  timeParts.push(`~${stop.minutes} min`);
                }
                if (typeof stop.tables === 'number' && stop.tables > 0) {
                  timeParts.push(`${stop.tables} ${stop.tables === 1 ? 'table' : 'tables'}`);
                }
                const timeLabel = timeParts.length > 0 ? timeParts.join(' \u00b7 ') : depthLabel;
                return (
                  <section key={stop.id} className={`rmap-stop ${explored ? 'rmap-stop-explored' : ''}`}>
                    <div className="rmap-stop-head">
                      <span className="rmap-stop-number">{idx + 1}</span>
                      <div className="rmap-stop-head-text">
                        {stop.kicker && <span className="rmap-stop-kicker">{stop.kicker}</span>}
                        <h2 className="rmap-stop-title">{stop.title}</h2>
                      </div>
                      <div className="rmap-stop-pills">
                        {explored && <span className="rmap-stop-explored-tag">Explored</span>}
                        {stop.payoff && (
                          <span className={`rmap-payoff rmap-payoff-${stop.payoff.toLowerCase()}`}>
                            {stop.payoff}
                          </span>
                        )}
                        {timeLabel && <span className="rmap-time-pill">{timeLabel}</span>}
                      </div>
                    </div>
                    <p className="rmap-stop-insight">{stop.insight}</p>
                    {stop.inside && stop.inside.length > 0 && (
                      <div className="rmap-inside">
                        <span className="rmap-inside-label">Inside</span>
                        <ul>
                          {stop.inside.map((item, itemIdx) => (
                            <li key={itemIdx}>
                              <button
                                type="button"
                                className="rmap-inside-link"
                                onClick={() => descend(item.section_title || stop.section_title || null, stop)}
                              >
                                {item.promise}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="rmap-stop-footer">
                      <button
                        type="button"
                        className="rmap-goto"
                        onClick={() => descend(stop.section_title || null, stop)}
                      >
                        Go to this section &rarr;
                      </button>
                    </div>
                  </section>
                );
              })}
            </div>

            {threads.length > 0 && (
              <div className="rmap-threads">
                <span className="rmap-threads-label">Threads</span>
                {threads.map((thread, idx) => {
                  const stopNumbers = (thread.stop_ids || [])
                    .map((id) => stops.findIndex((s) => s.id === id) + 1)
                    .filter((n) => n > 0);
                  return (
                    <div key={idx} className="rmap-thread">
                      <span className="rmap-thread-name">{thread.label}</span>
                      <span className="rmap-thread-question">{thread.question}</span>
                      {stopNumbers.length > 0 && (
                        <span className="rmap-thread-path">
                          Findings {stopNumbers.join(' \u2192 ')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <footer className="rmap-contract">
              <div className="rmap-contract-pips">
                {stops.map((stop) => (
                  <span
                    key={stop.id}
                    className={`rmap-pip ${exploredStopIds.has(stop.id) ? 'rmap-pip-on' : ''}`}
                  />
                ))}
              </div>
              <span className="rmap-contract-text">
                {exploredCount} of {stops.length} findings explored
              </span>
            </footer>
          </article>
        </div>
      </div>

      {descended && (
        <button type="button" className="rmap-back-pill" onClick={resurface}>
          <span className="rmap-back-pill-arrow">&#8963;</span> Back to the Brief
        </button>
      )}
    </>
  );
};

export default MapView;
